import React from 'react';
import { Alert, Box, Button, Chip, Grid, Paper, Typography } from '@mui/material';
import {
  PACK_BY_KEY,
  getPolicyAliasDefinition,
  type PackKey,
  type PolicyAliasDefinition,
} from '../../domain/policies/policyPacks';

export interface PackPolicyChooserStepProps {
  selectedPackKey: PackKey;
  selectedPolicyAlias: string;
  onSelectPolicy: (alias: string) => void;
  onChangeBack: () => void;
}

const PackPolicyChooserStep: React.FC<PackPolicyChooserStepProps> = ({
  selectedPackKey,
  selectedPolicyAlias,
  onSelectPolicy,
  onChangeBack,
}) => {
  const pack = PACK_BY_KEY[selectedPackKey];
  if (!pack) {
    return (
      <Alert severity="error">Pack not found: {selectedPackKey}</Alert>
    );
  }

  const selectableAliases: PolicyAliasDefinition[] = pack.policies.filter(
    (p) => !p.disabled && p.policyType,
  );
  const selectedDef = selectedPolicyAlias ? getPolicyAliasDefinition(selectedPolicyAlias) : undefined;
  const aliasGroups = selectableAliases.reduce<Array<{
    key: string;
    label: string;
    description?: string;
    policies: PolicyAliasDefinition[];
  }>>((acc, aliasDef) => {
    const key = String(aliasDef.groupLabel || 'All Rules');
    const existing = acc.find((group) => group.key === key);
    if (existing) {
      existing.policies.push(aliasDef);
      return acc;
    }
    acc.push({
      key,
      label: aliasDef.groupLabel || 'All Rules',
      description: aliasDef.groupDescription,
      policies: [aliasDef],
    });
    return acc;
  }, []);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Choose a rule in {pack.label}
        </Typography>
        <Button size="small" onClick={onChangeBack}>
          Change Pack
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {selectedDef
          ? `${pack.label} / ${selectedDef.label}. Next: configure where it runs and tune the rule.`
          : 'Select the specific protection you want to enable.'}
      </Typography>

      {selectedPackKey === 'worker_protection' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Worker Down is staged for early enablement only. Worker Fall remains hidden until its runtime, evidence, and validation gates are ready.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 2 }}>
        {aliasGroups.map((group) => (
          <Box key={group.key}>
            {aliasGroups.length > 1 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {group.label}
                </Typography>
                {group.description && (
                  <Typography variant="body2" color="text.secondary">
                    {group.description}
                  </Typography>
                )}
              </Box>
            )}
            <Grid container spacing={1.5}>
              {group.policies.map((aliasDef) => {
                const selected = selectedPolicyAlias === aliasDef.alias;
                return (
                  <Grid item xs={12} sm={6} key={aliasDef.alias}>
                    <Paper
                      onClick={() => onSelectPolicy(aliasDef.alias)}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'primary.50' : 'background.paper',
                        transition: 'border-color 0.15s, background-color 0.15s',
                        '&:hover': { borderColor: selected ? 'primary.main' : 'action.hover' },
                        height: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'start' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {aliasDef.label}
                        </Typography>
                        {selected && <Chip label="Selected" color="primary" size="small" />}
                      </Box>
                      {aliasDef.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {aliasDef.description}
                        </Typography>
                      )}
                      {aliasDef.scopeKind && (
                        <Chip
                          label={aliasDef.scopeKind === 'whole_view_optional' ? 'Whole camera or zones' : aliasDef.scopeKind === 'line' ? 'Line' : 'Zones'}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default PackPolicyChooserStep;
